import React, { useState, useEffect, Suspense } from 'react';
import IASideBar from './IASideBar';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import SearchAppBar from '../../common/Appbar';
import OutlinedCard from '../../common/MediaCard';
import Separator from '../../layout/Separator';
import ReviewsRoundedIcon from '@mui/icons-material/ReviewsRounded';
import auditService from '../../../services/AuditService';
import { useParams } from 'react-router-dom'; 
import * as Icons from '@mui/icons-material';

const AuditProjectSettings = () => {
    const [selectedProject, setSelectedProject] = useState([])
    const { id } = useParams();

    useEffect(() => {
        const fetchProjectbyID = async () => {
            try {
                const projectRecord = await auditService.fetchProjectsById(id)
                if (projectRecord) {
                    setSelectedProject(projectRecord);
                } else {
                    setSelectedProject([]);
                }
            } catch (fetchError) {
                console.error('Error fetching project:', fetchError);
                setSelectedProject([])
            }
        }
        fetchProjectbyID()
    }, [id]);

    const iconMapping = {
        planning_doc: Icons.ArticleRounded,
        folder_special: Icons.FolderSpecial,
        security: Icons.Security,
        cloud_upload: Icons.CloudUpload,
        topic: Icons.Topic,
        key: Icons.Key,
    };

    const menuItems = [
        {
            icon: 'folder_special',
            title: 'Workpapers',
            description: 'Create and manage audit documentation and working papers',
            to: `/audit/workpapers/${selectedProject.id}`,
            buttonLabel: 'Start Documentation'
        },
        {
            icon: 'security',
            title: 'Risk and Controls',
            description: 'Map and assess risks and controls for the audit',
            to: `/audit/riskandcontrolmapping/${selectedProject.id}`,
            buttonLabel: 'Manage'
        },
        {
            icon: 'cloud_upload',
            title: 'Document Requests',
            description: 'Create and track document requests for the audit',
            to: `/audit/auditrequests/${selectedProject.id}`,
            buttonLabel: 'Create and View'
        },
        {
            icon: 'planning_doc',
            title: 'Audit Planning',
            description: 'Manage phases, activities, and timeline for the audit project',
            to: `/audit/projects/${selectedProject.id}/planning`,
            buttonLabel: 'Plan'
        },
        {
            icon: 'topic',
            title: 'Reports',
            description: 'Generate and download audit reports',
            to: '/Applications',
            buttonLabel: 'Download'
        },
        {
            icon: 'key',
            title: 'Manage Access',
            description: 'Control user access and permissions',
            to: `/audit/manageaccess/${selectedProject.id}`,
            buttonLabel: 'Manage'
        }
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active': return { bg: '#f0f9ff', dot: '#0ea5e9', text: '#0284c7' };
            case 'Archived': return { bg: '#f8fafc', dot: '#64748b', text: '#475569' };
            case 'On-Hold': return { bg: '#fff1f2', dot: '#f43f5e', text: '#e11d48' };
            default: return { bg: '#fefce8', dot: '#facc15', text: '#ca8a04' };
        }
    };

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Box sx={{ 
                    padding: { xs: '16px', sm: '24px' },
                    maxWidth: '1600px',
                    margin: '0 auto'
                }}>
                    <mui.Breadcrumbs 
                        aria-label="breadcrumb"
                        sx={{
                            '& .MuiLink-root': {
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.secondary',
                                fontSize: '0.875rem',
                                transition: 'color 0.2s ease',
                                '&:hover': {
                                    color: 'primary.main'
                                }
                            }
                        }}
                    >
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/Audit/Projects">
                            Audit Projects
                        </mui.Link>
                        <mui.Typography color="text.primary">{selectedProject.AUDIT_NAME}</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        mb: 4, 
                        mt: 3,
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 2, sm: 0 }
                    }}>
                        <ReviewsRoundedIcon sx={{ 
                            fontSize: 28, 
                            color: 'primary.main', 
                            mr: { xs: 0, sm: 2.5 },
                            opacity: 0.9
                        }} />
                        <mui.Box sx={{ flex: 1 }}>
                            <mui.Typography 
                                variant="h4" 
                                component="h1" 
                                sx={{ 
                                    fontWeight: 600, 
                                    color: 'text.primary', 
                                    mb: 1,
                                    fontSize: { xs: '1.5rem', sm: '2rem' }
                                }}
                            >
                                {selectedProject.AUDIT_NAME}
                            </mui.Typography>
                            <mui.Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2,
                                flexWrap: 'wrap'
                            }}>
                                <mui.Chip
                                    size="small"
                                    label={selectedProject.AUDIT_TYPE || 'N/A'}
                                    sx={{ 
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                        color: 'primary.main',
                                        fontWeight: 500,
                                        borderRadius: '6px',
                                        height: '24px'
                                    }}
                                />
                                <mui.Box 
                                    sx={{ 
                                        display: 'inline-flex', 
                                        alignItems: 'center',
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: '6px',
                                        backgroundColor: getStatusColor(selectedProject.STATUS).bg,
                                        gap: 1
                                    }}
                                >
                                    <span style={{ 
                                        width: 6, 
                                        height: 6, 
                                        borderRadius: '50%',
                                        backgroundColor: getStatusColor(selectedProject.STATUS).dot,
                                        display: 'inline-block'
                                    }} />
                                    <mui.Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: getStatusColor(selectedProject.STATUS).text,
                                            fontSize: '0.8125rem',
                                            fontWeight: 500
                                        }}
                                    >
                                        {selectedProject.STATUS || 'N/A'}
                                    </mui.Typography>
                                </mui.Box>
                            </mui.Box>
                        </mui.Box>
                    </mui.Box>

                    <mui.Typography 
                        variant="body1" 
                        sx={{ 
                            mb: 4, 
                            color: 'text.secondary',
                            maxWidth: '800px',
                            lineHeight: 1.7,
                            fontSize: '0.9375rem'
                        }}
                    >
                        {selectedProject.AUDIT_DESCRIPTION}
                    </mui.Typography>

                    <mui.Grid container spacing={2.5}>
                        {menuItems.map((item, index) => (
                            <mui.Grid item xs={12} sm={6} md={4} key={index}>
                                <mui.Paper
                                    sx={{
                                        p: 2.5,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: 2,
                                        backgroundColor: '#ffffff',
                                        transition: 'all 0.2s ease',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        boxShadow: 'none',
                                        backgroundImage: 'linear-gradient(to bottom right, rgba(255,255,255, 0.9), rgba(255,255,255, 0.9))',
                                        backdropFilter: 'blur(8px)',
                                        border: '1px solid',
                                        borderColor: 'rgba(148, 163, 184, 0.1)',
                                        '&:hover': {
                                            borderColor: 'rgba(148, 163, 184, 0.2)',
                                            backgroundColor: '#fafafa',
                                            '& .card-icon': {
                                                color: 'primary.main',
                                                transform: 'scale(1.1) rotate(5deg)',
                                            },
                                            '& .card-button': {
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                            }
                                        }
                                    }}
                                >
                                    <mui.Box 
                                        className="card-icon"
                                        sx={{ 
                                            display: 'inline-flex',
                                            p: 1,
                                            borderRadius: 1.5,
                                            backgroundColor: 'rgba(148, 163, 184, 0.05)',
                                            width: 'fit-content',
                                            mb: 2,
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        {React.createElement(iconMapping[item.icon], { 
                                            sx: { 
                                                fontSize: 24,
                                                color: 'primary.light',
                                                transition: 'all 0.2s ease'
                                            }
                                        })}
                                    </mui.Box>

                                    <mui.Typography 
                                        variant="h6" 
                                        sx={{ 
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                            color: 'text.primary',
                                            mb: 1
                                        }}
                                    >
                                        {item.title}
                                    </mui.Typography>
                                    
                                    <mui.Typography 
                                        variant="body2" 
                                        sx={{ 
                                            mb: 2.5,
                                            flexGrow: 1,
                                            color: 'text.secondary',
                                            fontSize: '0.875rem',
                                            lineHeight: 1.5
                                        }}
                                    >
                                        {item.description}
                                    </mui.Typography>

                                    <mui.Button
                                        className="card-button"
                                        variant="outlined"
                                        href={item.to}
                                        size="small"
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            fontSize: '0.875rem',
                                            borderRadius: 1,
                                            py: 0.75,
                                            borderColor: 'rgba(148, 163, 184, 0.2)',
                                            color: 'text.primary',
                                            backgroundColor: 'transparent',
                                            transition: 'all 0.2s ease',
                                            alignSelf: 'flex-start',
                                            minWidth: '120px',
                                            '&:hover': {
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                                borderColor: 'primary.main'
                                            }
                                        }}
                                    >
                                        {item.buttonLabel}
                                    </mui.Button>
                                </mui.Paper>
                            </mui.Grid>
                        ))}
                    </mui.Grid>
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    return (
        <Suspense fallback="Loading...">
            <div>
                <IASideBar mainContent={customMainContent} />
            </div>
        </Suspense>
    );
}

export default AuditProjectSettings;